import type { AskResult, Citation, TraceLineProps } from '@patchbay/ui'
import { announceWalkthrough } from '../features/riley/walkthrough-event'
import { describeError, unwrap } from '../lib/api/client'
import type { Citation as ApiCitation, ChatThreadOut, InvestigationOut } from '../lib/api/generated'
import {
  byNumberApiV1CasesByNumberNumberGet,
  chatPostApiV1ChatThreadIdMessagesPost,
  chatStartApiV1ChatStartPost,
  investigateApiV1CasesCaseIdInvestigatePost,
} from '../lib/api/generated'

/**
 * The Ctrl+K peek's Ask mode (mockup 06, round 2: "Ask Riley returns an investigation result with
 * actions"). In a case: Riley's read-only Investigate on that case (`POST /cases/{id}/investigate`).
 * Elsewhere: one turn of the queue-scope chat (`POST /chat/start` + `/messages`). Either way the result
 * is a trace (one line per read), a finding and actions; a failure says what failed and that nothing
 * ran, never an empty "no findings" (P11).
 */

export type AskScope = { kind: 'case'; caseNumber: string } | { kind: 'queue' }

export interface AskActions {
  openCase: (caseNumber: string) => void
  openRail: () => void
  goHome: () => void
}

const KIND: Record<string, Citation['kind']> = {
  desk: 'desk',
  conduit: 'conduit',
  kb: 'kb',
  pbx: 'pbx',
  unifi: 'unifi',
  ninja: 'ninja',
  cambium: 'cambium',
  zabbix: 'zabbix',
  books: 'books',
}

export function toCitation(c: ApiCitation): Citation {
  return {
    label: c.label,
    kind: KIND[c.type] ?? 'other',
    internalOnly: c.internal_only ?? false,
    selfWrite: c.type === 'riley_writeback',
  }
}

/** Riley trace event `{seq, source, verb, target_label, ok, summary, citation}` → one TraceLine. */
export function toTraceLine(raw: Record<string, unknown>): TraceLineProps {
  const str = (v: unknown) => (typeof v === 'string' ? v : '')
  const source = str(raw.source) || 'read'
  const what = [str(raw.verb), str(raw.target_label)].filter(Boolean).join(' ')
  const summary = str(raw.summary)
  const citation = raw.citation as { type?: unknown } | null | undefined
  return {
    source: source.charAt(0).toUpperCase() + source.slice(1),
    detail: [what, summary].filter(Boolean).join(': ') || 'read',
    status: raw.ok === false ? 'error' : 'ok',
    selfCitation: citation?.type === 'riley_writeback',
  }
}

export function investigationToAsk(inv: InvestigationOut, caseNumber: string, actions: AskActions): AskResult {
  const trace = (inv.trace ?? []).map(toTraceLine)
  const failed = inv.status === 'failed' || inv.status === 'cancelled'
  const finding = failed
    ? `Investigation ${inv.status}${inv.caps_hit ? ` (cap hit: ${inv.caps_hit})` : ''}. Nothing was changed.`
    : inv.findings_md?.trim() ||
      (inv.status === 'done' ? 'Riley finished without a finding.' : `Investigation ${inv.status}; it continues in the rail.`)
  return {
    trace,
    finding,
    actions: [
      { label: 'Draft reply to customer', primary: true, onSelect: () => actions.openCase(caseNumber) },
      { label: 'Open in the Riley rail', onSelect: actions.openRail },
    ],
  }
}

export function chatToAsk(thread: ChatThreadOut, actions: AskActions): AskResult {
  const last = [...(thread.messages ?? [])].reverse().find((m) => m.role === 'riley')
  const walk = thread.walkthrough
  return {
    trace: [],
    finding: last?.content_md?.trim() || 'Riley has not answered yet; the conversation continues on Home.',
    citations: (last?.citations ?? []).map(toCitation),
    resultTables: last?.result_tables,
    reportCards: last?.report_cards,
    actions: walk?.current_case
      ? [
          {
            label: `Open case ${walk.position} of ${walk.total}`,
            primary: true,
            onSelect: () => actions.openCase(walk.current_case?.case_number ?? ''),
          },
          { label: 'Continue on Home', onSelect: actions.goHome },
        ]
      : [{ label: 'Continue on Home', primary: true, onSelect: actions.goHome }],
  }
}

export function isWalkRequest(question: string): boolean {
  return /(?:^|\b)(?:walk(?:\s+through)?|go\s+through)(?:\b|$)/i.test(question)
}

export function failedAsk(err: unknown, scope: AskScope, actions: AskActions): AskResult {
  return {
    trace: [{ source: 'Patchbay', detail: describeError(err), status: 'error' }],
    finding: `Riley could not answer: ${describeError(err)}. Nothing was run or changed.`,
    actions:
      scope.kind === 'case'
        ? [{ label: 'Open the case', primary: true, onSelect: () => actions.openCase(scope.caseNumber) }]
        : [{ label: 'Ask on Home', primary: true, onSelect: actions.goHome }],
  }
}

export function newClientMessageId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `m-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export async function askRiley(question: string, scope: AskScope, actions: AskActions): Promise<AskResult> {
  const effectiveScope: AskScope = isWalkRequest(question) ? { kind: 'queue' } : scope
  try {
    if (effectiveScope.kind === 'case') {
      const c = await unwrap(byNumberApiV1CasesByNumberNumberGet({ path: { number: effectiveScope.caseNumber } }))
      const inv = await unwrap(
        investigateApiV1CasesCaseIdInvestigatePost({
          path: { case_id: c.id },
          body: { question },
          headers: { Accept: 'application/json' },
        }),
      )
      return investigationToAsk(inv, effectiveScope.caseNumber, actions)
    }
    const thread = await unwrap(chatStartApiV1ChatStartPost({ body: { scope: 'queue', resume: true } }))
    const answered = await unwrap(
      chatPostApiV1ChatThreadIdMessagesPost({
        path: { thread_id: thread.id },
        body: { message: question, client_message_id: newClientMessageId() },
        headers: { Accept: 'application/json' },
      }),
    )
    if (answered.walkthrough !== undefined) announceWalkthrough(answered.walkthrough)
    return chatToAsk(answered, actions)
  } catch (err) {
    return failedAsk(err, effectiveScope, actions)
  }
}
